import { Injectable } from '@angular/core';
import {
  FormBuilder, FormGroup, FormControl,
  AbstractControl, ValidatorFn, AsyncValidatorFn, Validators
} from '@angular/forms';
import { FormSchema } from '../models/form-schema.model';
import { FieldConfig } from '../models/field-config.model';
import { ValidationRule } from '../models/validation.model';
import { ValidatorFactoryService } from '../validators/validator-factory.service';
import { CustomValidators } from '../validators/custom-validators';

@Injectable({ providedIn: 'root' })
export class AdminFormBuilderService {

  constructor(
    private fb: FormBuilder,
    private validatorFactory: ValidatorFactoryService
  ) {}

  buildForm(
    schema: FormSchema,
    initialData?: Record<string, unknown>
  ): FormGroup {
    const controls: Record<string, AbstractControl> = {};
    for (const section of schema.sections) {
      for (const field of section.fields) {
        const isMultipleCustom = field.type === 'custom' &&
          !!(field.customProps as Record<string, unknown>)?.['multiple'];
        const fallback = (field.type === 'multiselect' || isMultipleCustom) ? [] : null;
        const value = initialData?.[field.id]
          ?? field.defaultValue ?? fallback;
        const syncValidators  = this.buildSyncValidators(field.validations ?? []);
        const asyncValidators = this.buildAsyncValidators(field.validations ?? []);

        controls[field.id] = new FormControl(
          { value, disabled: field.disabled ?? false },
          syncValidators,
          asyncValidators
        );
      }
    }
    return this.fb.group(controls);
  }

  patchValues(
    form: FormGroup,
    data: Record<string, unknown>
  ): void {
    form.patchValue(data, { emitEvent: false });
  }

  resetForm(form: FormGroup, schema: FormSchema): void {
    const defaults: Record<string, unknown> = {};
    for (const section of schema.sections) {
      for (const field of section.fields) {
        const isMultipleCustomReset = field.type === 'custom' &&
          !!(field.customProps as Record<string, unknown>)?.['multiple'];
        defaults[field.id] = field.defaultValue ?? ((field.type === 'multiselect' || isMultipleCustomReset) ? [] : null);
      }
    }
    form.reset(defaults);
  }

  getFieldControl(form: FormGroup, fieldId: string): FormControl {
    const ctrl = form.get(fieldId);
    if (!ctrl) throw new Error(
      `[AdminForms] Control '${fieldId}' no existe`
    );
    return ctrl as FormControl;
  }

  // renombrado — ahora maneja custom también
  private buildSyncValidators(rules: ValidationRule[]): ValidatorFn[] {
    return rules
      .filter(r => r.type !== 'async')
      .map(rule => {
        switch (rule.type) {
          case 'required':  return Validators.required;
          case 'email':     return CustomValidators.strictEmail();
          case 'minLength': return Validators.minLength(rule.value as number);
          case 'maxLength': return Validators.maxLength(rule.value as number);
          case 'min':       return Validators.min(rule.value as number);
          case 'max':       return Validators.max(rule.value as number);
          case 'pattern':   return Validators.pattern(rule.value as string | RegExp);
          case 'minSelect': return CustomValidators.minSelect(rule.value as number);
          case 'maxSelect': return CustomValidators.maxSelect(rule.value as number);
          case 'custom':
            return rule.validatorFn
              ? this.validatorFactory.resolveSync(rule.validatorFn, rule.value)
              : null;
          default: return null;
        }
      })
      .filter((v): v is ValidatorFn => v !== null);
  }

  private buildAsyncValidators(rules: ValidationRule[]): AsyncValidatorFn[] {
    return rules
      .filter(r => r.type === 'async')
      .map(r => r.validatorFn
        ? this.validatorFactory.resolveAsync(r.validatorFn, r.value)
        : null
      )
      .filter((v): v is AsyncValidatorFn => v !== null);
  }
}